import { useEffect, useRef, type ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  Clock,
  Loader2,
  ShieldCheck,
  WifiOff,
  XCircle,
} from 'lucide-react'
import type { ProtectionState } from '@shared/types'
import { PROTECTION_STATE_LABELS } from '@shared/domain/protection'

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}

const STATE_STYLES: Record<ProtectionState, { chip: string; icon: ReactNode }> = {
  active: { chip: 'bg-ok-50 text-ok-700', icon: <ShieldCheck aria-hidden="true" className="h-4 w-4" /> },
  expiring_soon: { chip: 'bg-warn-50 text-warn-700', icon: <Clock aria-hidden="true" className="h-4 w-4" /> },
  expired: { chip: 'bg-bad-50 text-bad-700', icon: <XCircle aria-hidden="true" className="h-4 w-4" /> },
  needs_confirmation: {
    chip: 'bg-warn-50 text-warn-700',
    icon: <AlertTriangle aria-hidden="true" className="h-4 w-4" />,
  },
  unknown: { chip: 'bg-sand-100 text-ink-soft', icon: <CircleHelp aria-hidden="true" className="h-4 w-4" /> },
}

/** Status se nikad ne prenosi samo bojom — uvijek ima ikonu i tekst. */
export function StatusChip({ state, label }: { state: ProtectionState; label?: string }) {
  const style = STATE_STYLES[state]
  return (
    <span className={cn('chip', style.chip)}>
      {style.icon}
      {label ?? PROTECTION_STATE_LABELS[state]}
    </span>
  )
}

export function Section({
  title,
  action,
  children,
  className,
}: {
  title?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('mb-6', className)}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {title && <h2 className="text-[15px] font-semibold text-ink-soft">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

export function LoadingState({ label = 'Učitavanje…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-ink-muted" role="status" aria-live="polite">
      <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
      <span>{label}</span>
    </div>
  )
}

export function ErrorState({
  message,
  offline,
  onRetry,
}: {
  message: string
  offline?: boolean
  onRetry?: () => void
}) {
  return (
    <div className="card p-5 text-center" role="alert">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-bad-50 text-bad-600">
        {offline ? <WifiOff aria-hidden="true" className="h-5 w-5" /> : <AlertTriangle aria-hidden="true" className="h-5 w-5" />}
      </div>
      <p className="text-[15px] font-semibold">{offline ? 'Niste povezani' : 'Nešto je pošlo po zlu'}</p>
      <p className="muted mt-1">{message}</p>
      {onRetry && (
        <button type="button" className="btn-secondary mt-4" onClick={onRetry}>
          Pokušaj ponovno
        </button>
      )}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  media,
}: {
  title: string
  description: string
  action?: ReactNode
  icon?: ReactNode
  media?: ReactNode
}) {
  return (
    <div className="card px-6 py-10 text-center">
      {media ?? (
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          {icon ?? <CheckCircle2 aria-hidden="true" className="h-6 w-6" />}
        </div>
      )}
      <h2 className="text-lg">{title}</h2>
      <p className="muted mx-auto mt-2 max-w-sm">{description}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  )
}

/** Modal s ispravnim upravljanjem fokusom i tipkom Escape. */
export function Modal({
  title,
  children,
  onClose,
  footer,
}: {
  title: string
  children: ReactNode
  onClose: () => void
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const node = ref.current
    const focusable = node?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    focusable?.[0]?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !node) return
      const items = node.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previouslyFocused.current?.focus()
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-lg rounded-t-3xl bg-white p-5 shadow-lift sm:rounded-3xl"
      >
        <h2 className="text-lg">{title}</h2>
        <div className="mt-3">{children}</div>
        {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  )
}

export function Disclaimer() {
  return (
    <p className="muted mt-6 rounded-xl bg-sand-50 p-3 leading-relaxed">
      Informacije u Računku služe za organizaciju dokumentacije i ne predstavljaju pravni savjet.
    </p>
  )
}
